import type { InDesignMasterTemplate } from "shared-types";
import type { RenditionS3FileLocation } from "shared-types/indesign";

import {
    RESOURCE_MANAGER_STATUS,
    getResourceManagerResourceById,
    getResourceStatus,
    resourceManagerRequest
} from "./resourceManager";

const POLL_INTERVAL_MS = 2000;

const INDESIGN_UPLOAD_POLL_INTERVAL_MS = 2000;

export async function pollInDesignUpload(resourceId: string) {
    return new Promise<void>((resolve, reject) => {
        async function pollApi() {
            try {
                const { data: resource } = await getResourceManagerResourceById({ resourceId });

                switch (getResourceStatus(resource)) {
                    case RESOURCE_MANAGER_STATUS.IN_PROGRESS:
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        setTimeout(pollApi, INDESIGN_UPLOAD_POLL_INTERVAL_MS);
                        break;

                    case RESOURCE_MANAGER_STATUS.AVAILABLE:
                        resolve();
                        break;

                    case RESOURCE_MANAGER_STATUS.FAILED:
                    default: {
                        const failure = resource.output?.fail;
                        reject(new Error(typeof failure === "string" ? failure : "Failed to upload InDesign template"));
                    }
                }
            } catch (error) {
                reject(error instanceof Error ? error : new Error("Failed to upload InDesign template"));
            }
        }

        void pollApi();
    });
}

export type RenditionScriptOutput = {
    s3FileLocations: RenditionS3FileLocation[];
};

export type PreviewLayerManagement = {
    font?: string;
    fontSize?: number;
    fontStyle?: string;
    letterSpacing?: number;
    lineHeight?: number;
    fontColor?: string;
    visible?: boolean;
};

export type PreviewSharedOverride = {
    itemName: string;
    editableType?: "text" | "image";
    value?: string;
    bucket?: string;
    key?: string;
    assetDestination?: string;
    layerManagement?: PreviewLayerManagement;
};

export type RenditionRequestBody = {
    type: "ADOBE_INDESIGN_PREVIEW";
    propBasedBudget?: string;
    input: {
        clientId: string;
        fonts?: { bucket?: string; key?: string; name?: string }[];
        sourceDocuments: { bucket: string; key: string }[];
        outputFormat: "png" | "indd";
        targetSize?: { width: number; height: number; unit?: string };
        sharedOverrides?: PreviewSharedOverride[];
    };
};

function createAbortError() {
    return new DOMException("InDesign preview request was cancelled", "AbortError");
}

function throwIfAborted(signal: AbortSignal) {
    if (signal.aborted) {
        throw createAbortError();
    }
}

function getPreviewImageUrl(output?: RenditionScriptOutput) {
    const renderedFile =
        output?.s3FileLocations?.find(fileLocation => fileLocation.signedUrl) || output?.s3FileLocations?.[0];

    return renderedFile?.signedUrl || null;
}

function sleep(durationMs: number, signal: AbortSignal) {
    return new Promise<void>((resolve, reject) => {
        if (signal.aborted) {
            reject(createAbortError());
            return;
        }

        // eslint-disable-next-line prefer-const
        let timeoutId: number;

        const abortHandler = () => {
            window.clearTimeout(timeoutId);
            reject(createAbortError());
        };

        timeoutId = window.setTimeout(() => {
            signal.removeEventListener("abort", abortHandler);
            resolve();
        }, durationMs);

        signal.addEventListener("abort", abortHandler, { once: true });
    });
}

async function pollRenditionRequest(requestClientId: string, requestBody: RenditionRequestBody, signal: AbortSignal) {
    for (;;) {
        throwIfAborted(signal);
        await sleep(POLL_INTERVAL_MS, signal);
        throwIfAborted(signal);

        const response = await resourceManagerRequest<RenditionScriptOutput>({
            id: requestClientId,
            body: requestBody,
            signal
        });
        const resource = response.data;

        throwIfAborted(signal);

        if (resource.status === RESOURCE_MANAGER_STATUS.IN_PROGRESS) {
            continue;
        }

        if (resource.status === RESOURCE_MANAGER_STATUS.BUDGET_LIMIT_HIT) {
            throw new Error("Your InDesign usage limit has been reached.");
        }

        if (resource.status === RESOURCE_MANAGER_STATUS.FAILED) {
            const failure = resource.output?.fail;
            throw new Error(typeof failure === "string" ? failure : "Failed to render InDesign preview");
        }

        return resource.output?.success || null;
    }
}

export async function requestIndesignRenditionOutput({
    signal = new AbortController().signal,
    clientId,
    sourceDocuments,
    renditionRequestBody,
    missingClientMessage = "Missing client id for InDesign preview request",
    missingSourceDocumentsMessage = "No persisted source documents available for InDesign preview",
    failureMessage = "Failed to render InDesign preview"
}: {
    signal?: AbortSignal;
    clientId?: string;
    sourceDocuments: InDesignMasterTemplate["sourceDocuments"];
    renditionRequestBody: RenditionRequestBody;
    missingClientMessage?: string;
    missingSourceDocumentsMessage?: string;
    failureMessage?: string;
}) {
    if (!clientId) {
        throw new Error(missingClientMessage);
    }

    if (!sourceDocuments.length) {
        throw new Error(missingSourceDocumentsMessage);
    }

    throwIfAborted(signal);

    const response = await resourceManagerRequest<RenditionScriptOutput>({
        id: clientId,
        body: renditionRequestBody,
        signal
    });

    const resource = response.data;

    throwIfAborted(signal);

    if (resource.status === RESOURCE_MANAGER_STATUS.BUDGET_LIMIT_HIT) {
        throw new Error("Your InDesign usage limit has been reached.");
    }

    if (resource.status === RESOURCE_MANAGER_STATUS.FAILED) {
        const failure = resource.output?.fail;
        throw new Error(typeof failure === "string" ? failure : failureMessage);
    }

    const output =
        resource.status === RESOURCE_MANAGER_STATUS.AVAILABLE
            ? resource.output.success
            : await pollRenditionRequest(clientId, renditionRequestBody, signal);

    throwIfAborted(signal);

    return output;
}

export async function requestIndesignPreview({
    signal,
    clientId,
    sourceDocuments,
    renditionRequestBody
}: {
    signal: AbortSignal;
    clientId?: string;
    sourceDocuments: InDesignMasterTemplate["sourceDocuments"];
    renditionRequestBody: RenditionRequestBody;
}) {
    const output = await requestIndesignRenditionOutput({
        signal,
        clientId,
        sourceDocuments,
        renditionRequestBody
    });

    const signedPreviewUrl = getPreviewImageUrl(output || undefined);

    throwIfAborted(signal);

    if (!signedPreviewUrl) {
        throw new Error("InDesign preview resource did not return a signed preview image");
    }

    return signedPreviewUrl;
}
